import React, { useContext, useState, useEffect } from 'react';
import AuthContext from '../../context/AuthContext';
import axios from 'axios';
import {
    Box,
    Button,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Icon,
    Card,
    CardBody,
    Flex,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Badge,
    useBreakpointValue,
    SimpleGrid,
    Stat,
    StatLabel,
    StatNumber,
    Tag,
    Wrap,
    WrapItem,
    useToast,
    CircularProgress,
    CircularProgressLabel,
    Input,
    InputGroup,
    InputLeftElement,
    IconButton,
    Collapse,
} from '@chakra-ui/react';
import {
    FaChartLine,
    FaSync,
    FaUsers,
    FaInfoCircle,
    FaLightbulb,
    FaBrain,
    FaSearch,
} from 'react-icons/fa';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import EmptyStateCard from '../../compnents/EmptyStateCard';
import ScoreFreshnessBadge from '../../compnents/ScoreFreshnessBadge';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

function DashboardRMPriorityScores() {
    const { token } = useContext(AuthContext);
    const [priorityData, setPriorityData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedCustomer, setExpandedCustomer] = useState(null);
    const toast = useToast();

    const statSize = useBreakpointValue({ base: 'sm', md: 'md' });

    const fetchPriorityScores = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/content/rm-priority-scores`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setPriorityData(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            console.error('Error fetching priority scores:', err);
            const msg = err.response?.data?.message || 'Failed to fetch priority scores';
            setError(msg);
            toast({
                title: 'Error',
                description: msg,
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) fetchPriorityScores();
        // eslint-disable-next-line
    }, [token]);

    const getUrgencyColor = (urgencyLevel) => {
        const level = urgencyLevel?.toLowerCase();
        if (level === 'urgent' || level === 'high') return 'red';
        if (level === 'medium' || level === 'moderate') return 'orange';
        return 'green';
    };

    const getPriorityColor = (score) => {
        if (score >= 8) return 'red';
        if (score >= 6) return 'orange';
        if (score >= 4) return 'yellow';
        return 'green';
    };

    // Toggle customer details
    const toggleCustomer = (customerId) => {
        if (expandedCustomer === customerId) {
            setExpandedCustomer(null);
        } else {
            setExpandedCustomer(customerId);
        }
    };

    // Handle search
    const handleSearch = () => {
        // Search is handled by filteredPriorityData already
    };

    // Stats
    const avgPriority =
        priorityData.length > 0
            ? (priorityData.reduce((sum, c) => sum + c.priority_score, 0) / priorityData.length).toFixed(2)
            : 0;

    const highUrgencyCount = priorityData.filter(
        (c) => c.urgency_level?.toLowerCase() === 'high' || c.urgency_level?.toLowerCase() === 'urgent'
    ).length;

    const filteredPriorityData = priorityData.filter(customer =>
        customer.customer_id.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Box minH="100vh" width="100%" pb={{ base: '90px', md: '20px' }}>
            <Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 3, md: 6 }}>
                <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                    <Box textAlign="center" pt={{ base: 2, md: 4 }}>
                        <Heading
                            size={{ base: 'lg', md: 'xl' }}
                            bgGradient="linear(to-r, brand.navy, brand.tealDark)"
                            bgClip="text"
                            mb={2}
                            fontWeight="extrabold"
                        >
                            <HStack justify="center" spacing={2}>
                                <Icon as={FaBrain} boxSize={{ base: 6, md: 8 }} />
                                <Text>Customer Priority Scores</Text>
                            </HStack>
                        </Heading>
                        <Text color="gray.600" fontSize={{ base: 'sm', md: 'md' }}>
                            Customers ranked by the FinanSee priority engine across behavioral and risk factors
                        </Text>
                    </Box>

                    {/* Summary Cards */}
                    {!loading && (
                        <SimpleGrid columns={{ base: 3, md: 3 }} spacing={{ base: 3, md: 4 }}>
                            {[
                                { icon: FaUsers, label: 'Total Customers', value: priorityData.length, color: 'teal' },
                                { icon: FaChartLine, label: 'Avg Priority', value: avgPriority, color: 'blue' },
                                { icon: FaInfoCircle, label: 'High Urgency', value: highUrgencyCount, color: 'orange' },
                            ].map((stat, i) => (
                                <Card key={i} bg="white" borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.100">
                                    <CardBody p={{ base: 4, md: 5 }}>
                                        <Stat size={statSize}>
                                            <HStack spacing={2} mb={1}>
                                                <Icon as={stat.icon} color={`${stat.color}.500`} boxSize={{ base: 4, md: 5 }} />
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                                                    {stat.label}
                                                </StatLabel>
                                            </HStack>
                                            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={`${stat.color}.600`}>
                                                {stat.value}
                                            </StatNumber>
                                        </Stat>
                                    </CardBody>
                                </Card>
                            ))}
                        </SimpleGrid>
                    )}

                    {error && (
                        <Alert status="error" borderRadius="xl" variant="left-accent">
                            <AlertIcon />
                            <Box>
                                <AlertTitle>Error!</AlertTitle>
                                <AlertDescription>{error}</AlertDescription>
                            </Box>
                        </Alert>
                    )}

                    {/* Search and Refresh */}
                    <Card bg="white" borderRadius="lg" boxShadow="md">
                        <CardBody>
                            <HStack spacing={4}>
                                <InputGroup flex={1}>
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={FaSearch} color="gray.400" />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Search by Customer ID..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                        borderColor="gray.300"
                                        _focus={{ borderColor: 'brand.teal', boxShadow: '0 0 0 1px var(--chakra-colors-brand-teal)' }}
                                    />
                                </InputGroup>
                                <Button
                                    leftIcon={<FaSync />}
                                    colorScheme="teal"
                                    onClick={fetchPriorityScores}
                                    isLoading={loading}
                                    _hover={{ bg: 'brand.navy' }}
                                >
                                    Refresh
                                </Button>
                            </HStack>
                        </CardBody>
                    </Card>

                    {/* Customer List */}
                    {loading ? (
                        <Flex justify="center" align="center" py={20}>
                            <VStack spacing={4}>
                                <Spinner size="xl" color="brand.tealDark" thickness="4px" />
                                <Text color="gray.500" fontSize="sm">Loading priority scores...</Text>
                            </VStack>
                        </Flex>
                    ) : filteredPriorityData.length === 0 ? (
                        <Card bg="white" borderRadius="lg" boxShadow="md">
                            <CardBody>
                                <EmptyStateCard
                                    icon={searchTerm ? FaSearch : FaChartLine}
                                    title={searchTerm ? 'No customers match this search' : 'No priority scores available yet'}
                                    description={
                                        searchTerm
                                            ? `We could not find any customers matching "${searchTerm}".`
                                            : 'Priority scores will appear here after the latest customer data has been processed for your RM workspace.'
                                    }
                                    hint={
                                        searchTerm
                                            ? 'Try a broader customer search or clear it to return to the full list.'
                                            : 'Refresh after a new scoring run or after updated data has been uploaded.'
                                    }
                                    borderColor="brand.divider"
                                    titleColor="gray.700"
                                    descriptionColor="gray.600"
                                    hintColor="gray.500"
                                    iconColor="brand.tealDark"
                                    maxW="full"
                                >
                                    {searchTerm ? (
                                        <Button
                                            variant="outline"
                                            borderColor="brand.teal"
                                            color="brand.tealDark"
                                            _hover={{ bg: 'brand.tealSoft' }}
                                            onClick={() => setSearchTerm('')}
                                        >
                                            Clear search
                                        </Button>
                                    ) : (
                                        <Button colorScheme="teal" onClick={fetchPriorityScores}>
                                            Refresh scores
                                        </Button>
                                    )}
                                </EmptyStateCard>
                            </CardBody>
                        </Card>
                    ) : (
                        <VStack spacing={4} align="stretch">
                            {filteredPriorityData.map((customer, index) => (
                                <Card
                                    key={customer.customer_id || index}
                                    bg="white"
                                    borderRadius="lg"
                                    boxShadow="md"
                                    transition="all 0.2s"
                                    _hover={{ boxShadow: 'lg' }}
                                >
                                    <CardBody>
                                        <Flex justify="space-between" align="center">
                                            <VStack align="start" spacing={2} flex={1}>
                                                <HStack spacing={3} flexWrap="wrap">
                                                    <Heading size="sm" color="brand.navy">
                                                        {customer.customer_id}
                                                    </Heading>
                                                    <Badge
                                                        colorScheme={getUrgencyColor(customer.urgency_level)}
                                                        px={3}
                                                        py={1}
                                                        borderRadius="full"
                                                        fontSize="xs"
                                                        fontWeight="bold"
                                                    >
                                                        {customer.urgency_level}
                                                    </Badge>
                                                    <ScoreFreshnessBadge freshness={customer.freshness} />
                                                </HStack>
                                                <HStack spacing={3} align="center">
                                                    <CircularProgress
                                                        value={(customer.priority_score / 10) * 100}
                                                        color={customer.actionable === false ? 'gray.400' : `${getPriorityColor(customer.priority_score)}.400`}
                                                        size="50px"
                                                        thickness="8px"
                                                        opacity={customer.actionable === false ? 0.45 : 1}
                                                    >
                                                        <CircularProgressLabel fontSize="xs" fontWeight="bold">
                                                            {customer.priority_score.toFixed(1)}
                                                        </CircularProgressLabel>
                                                    </CircularProgress>
                                                    <VStack align="start" spacing={0}>
                                                        <Text fontSize="xs" color="gray.500" fontWeight="medium">
                                                            {customer.actionable === false ? 'Reference only' : 'Priority Score'}
                                                        </Text>
                                                        <Text fontSize="xs" color="gray.400">
                                                            Max: 10.0
                                                        </Text>
                                                    </VStack>
                                                </HStack>
                                            </VStack>
                                            <IconButton
                                                icon={
                                                    expandedCustomer === customer.customer_id ? (
                                                        <ChevronUpIcon />
                                                    ) : (
                                                        <ChevronDownIcon />
                                                    )
                                                }
                                                onClick={() => toggleCustomer(customer.customer_id)}
                                                colorScheme="teal"
                                                variant="ghost"
                                                aria-label="Toggle details"
                                            />
                                        </Flex>

                                        <Collapse in={expandedCustomer === customer.customer_id} animateOpacity>
                                            <Box mt={6} pt={6} borderTop="1px solid" borderColor="gray.200">
                                                <VStack align="stretch" spacing={4}>
                                                    {/* Recommended Action */}
                                                    {customer.recommended_action && (
                                                        <Card bg="brand.tealSoft" borderRadius="md" boxShadow="sm">
                                                            <CardBody>
                                                                <HStack spacing={2} mb={2}>
                                                                    <Icon as={FaLightbulb} color="brand.teal" boxSize={5} />
                                                                    <Text fontWeight="bold" color="gray.700">Recommended Action</Text>
                                                                </HStack>
                                                                <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                                                                    {customer.recommended_action}
                                                                </Text>
                                                            </CardBody>
                                                        </Card>
                                                    )}

                                                    {/* Top Biases */}
                                                    {customer.top_biases?.length > 0 && (
                                                        <Card bg="brand.tealSoft" borderRadius="md" boxShadow="sm">
                                                            <CardBody>
                                                                <HStack spacing={2} mb={3}>
                                                                    <Icon as={FaBrain} color="brand.tealDark" boxSize={5} />
                                                                    <Text fontWeight="bold" color="gray.700">Key Behavioral Biases</Text>
                                                                </HStack>
                                                                <Wrap spacing={2}>
                                                                    {customer.top_biases.map((bias, i) => (
                                                                        <WrapItem key={i}>
                                                                            <Tag colorScheme="teal" size="md" variant="subtle" borderRadius="full">
                                                                                {bias.bias_name || 'N/A'}
                                                                            </Tag>
                                                                        </WrapItem>
                                                                    ))}
                                                                </Wrap>
                                                            </CardBody>
                                                        </Card>
                                                    )}

                                                    {/* Breakdown Details */}
                                                    {customer.breakdown && (
                                                        <Card bg="gray.50" borderRadius="md" boxShadow="sm">
                                                            <CardBody>
                                                                <HStack spacing={2} mb={3}>
                                                                    <Icon as={FaInfoCircle} color="gray.600" boxSize={5} />
                                                                    <Text fontWeight="bold" color="gray.700">Score Breakdown</Text>
                                                                </HStack>
                                                                <VStack align="stretch" spacing={2}>
                                                                    {Object.entries(customer.breakdown).map(([key, value]) => (
                                                                        <Flex key={key} justify="space-between" fontSize="sm">
                                                                            <Text textTransform="capitalize" color="gray.600">
                                                                                {key.replace(/_/g, ' ')}:
                                                                            </Text>
                                                                            <Text fontWeight="semibold" color="gray.800">
                                                                                {typeof value === 'number' ? value.toFixed(2) : value}
                                                                            </Text>
                                                                        </Flex>
                                                                    ))}
                                                                </VStack>
                                                            </CardBody>
                                                        </Card>
                                                    )}
                                                </VStack>
                                            </Box>
                                        </Collapse>
                                    </CardBody>
                                </Card>
                            ))}
                        </VStack>
                    )}

                    {/* Summary footer */}
                    {!loading && filteredPriorityData.length > 0 && (
                        <Card bg="white" borderRadius="lg" boxShadow="md">
                            <CardBody>
                                <Text fontSize="sm" color="gray.600" textAlign="center">
                                    Showing {filteredPriorityData.length} of {priorityData.length} customers
                                </Text>
                            </CardBody>
                        </Card>
                    )}
                </VStack>
            </Container>
        </Box>
    );
}

export default DashboardRMPriorityScores;
